import { FactiiiRequirement } from './factiiis';

/**
 * The plain-English copy for every requirement, shared by the web and mobile
 * create flows so a label can never drift between them. Icons are named, not
 * imported: web maps a name to `lucide-react`, mobile to `lucide-react-native`,
 * and the two packages export the same set.
 */
export type RequirementIconName =
  | 'BookOpen'
  | 'Briefcase'
  | 'Camera'
  | 'Clock'
  | 'EyeOff'
  | 'FileText'
  | 'Fingerprint'
  | 'FolderSearch'
  | 'Hash'
  | 'Home'
  | 'Link'
  | 'List'
  | 'MapPin'
  | 'Shield'
  | 'User';

export type RequirementCopy = {
  value: FactiiiRequirement | null;
  label: string;
  description: string;
  icon: RequirementIconName | null;
};

/** What a tag carries as its value. Single-choice: a tag holds one value. */
export const dataRequirementCopy: RequirementCopy[] = [
  {
    label: 'Nothing extra',
    value: null,
    icon: null,
    description: 'Tagging is enough — no value to fill in'
  },
  {
    label: 'Add a Number',
    value: FactiiiRequirement.INTEGER_REQUIRED,
    icon: 'Hash',
    description:
      'Include a number with your post (like a count, score, or rating)'
  },
  {
    label: 'Community Help',
    value: FactiiiRequirement.DISAPPEARING_DATA,
    icon: 'Shield',
    description:
      'Your post stays public until someone helps by adding the info you need'
  },
  {
    label: 'From a Person',
    value: FactiiiRequirement.HUMAN_SOURCE,
    icon: 'User',
    description:
      'Info from someone who saw it happen or experienced it firsthand'
  },
  {
    label: 'From Government',
    value: FactiiiRequirement.GOVERNMENT_SOURCE,
    icon: 'Home',
    description:
      'Official info from government agencies, public records, or official reports'
  },
  {
    label: 'From a Company',
    value: FactiiiRequirement.ENTERPRISE_SOURCE,
    icon: 'Briefcase',
    description: 'Info from businesses, organizations, or official institutions'
  },
  {
    label: 'Anonymous Source',
    value: FactiiiRequirement.ANONYMOUS_SOURCE,
    icon: 'EyeOff',
    description:
      'Info from an unidentified source when the identity needs to be protected'
  },
  {
    label: 'Extra Details',
    value: FactiiiRequirement.DATA_REQUIRED,
    icon: 'FileText',
    description: "You'll need to write some additional info"
  }
];

/** How a place is captured. Single-choice: one kind of place per tag. */
export const spaceTimeRequirementCopy: RequirementCopy[] = [
  {
    label: 'No place',
    value: null,
    icon: null,
    description: "Tags don't record where it happened"
  },
  {
    label: 'Add Location',
    value: FactiiiRequirement.LOCATION_REQUIRED,
    icon: 'MapPin',
    description: 'Pick a location to attach to your post'
  },
  {
    label: 'Use My Location',
    value: FactiiiRequirement.DEVICE_LOCATION,
    icon: 'MapPin',
    description: 'Automatically uses your current location from GPS'
  },
  {
    label: 'Nearby Location',
    value: FactiiiRequirement.APPROXIMATE_LOCATION,
    icon: 'MapPin',
    description: 'Uses your general area (within ~0.5 miles) for privacy'
  }
];

/** Everything else a post or tag can be made to carry. Pick any. */
export const additionalRequirementCopy: RequirementCopy[] = [
  {
    value: FactiiiRequirement.MEDIA_REQUIRED,
    label: 'Add Photo or Video',
    description: 'Include a photo, video, or other media with your post',
    icon: 'Camera'
  },
  {
    value: FactiiiRequirement.VIDEO_REQUIRED,
    label: 'Add a Video',
    description: 'Include a video or screen recording with your post',
    icon: 'Camera'
  },
  {
    value: FactiiiRequirement.CONTENT_REQUIRED,
    label: 'Write Something',
    description: 'Add some text to describe or explain your post',
    icon: 'FileText'
  },
  {
    value: FactiiiRequirement.WIKI,
    label: 'Quick Fact',
    description: 'Keep it short - max 120 characters for quick, snappy facts',
    icon: 'Hash'
  },
  {
    value: FactiiiRequirement.ROOT,
    label: 'Run on My Files',
    description:
      'AI tags this automatically on your OneDrive files. Write the description as the instruction: say when it should be applied, and what to read off the file for any other requirements you add.',
    icon: 'FolderSearch'
  },
  {
    value: FactiiiRequirement.NO_POST_DUPLICATES,
    label: 'One Per Post',
    description: 'This tag can only be used once on each post',
    icon: 'Shield'
  },
  {
    value: FactiiiRequirement.POST_REFERENCE_REQUIRED,
    label: 'Link a Post',
    description: 'Reference another post to connect your content',
    icon: 'Link'
  },
  {
    value: FactiiiRequirement.SOURCE_REQUIRED,
    label: 'Add a Source',
    description: 'Include a link or reference to back up the info',
    icon: 'BookOpen'
  },
  {
    value: FactiiiRequirement.POSTFACTIII_MODERATION_REQUIRED,
    label: 'Needs Approval',
    description:
      'A moderator will review this tag before it appears on your post',
    icon: 'Shield'
  },
  {
    value: FactiiiRequirement.CAMERA,
    label: 'Take a Photo',
    description: 'Snap a new photo using your camera',
    icon: 'Camera'
  },
  {
    value: FactiiiRequirement.MEDIA_MATCH,
    label: 'Media Match',
    description: 'Group one image with similar posts and make them searchable',
    icon: 'Fingerprint'
  }
];

// Labels for requirements that sit in none of the lists above: TIME_REQUIRED
// is toggled on its own by the create form's "Place and time" screen, LIST is
// set by the board layout and never picked. Anything missing here is dropped
// outright by the factiii page, which is why time/list factiiis once rendered
// an empty section.
export const systemRequirementCopy: RequirementCopy[] = [
  {
    value: FactiiiRequirement.TIME_REQUIRED,
    label: 'Pick a Time',
    description: 'Attach a date and time to your post',
    icon: 'Clock'
  },
  {
    value: FactiiiRequirement.LIST,
    label: 'Board List',
    description: 'Renders this factiii as a list on a board space',
    icon: 'List'
  }
];

const allRequirementCopy: RequirementCopy[] = [
  ...dataRequirementCopy,
  ...spaceTimeRequirementCopy,
  ...additionalRequirementCopy,
  ...systemRequirementCopy
];

export const requirementLabel = (requirement: string): string =>
  allRequirementCopy.find((option) => option.value === requirement)?.label ??
  requirement;
